import { parseScaleFrame, ScaleStability, splitScaleFrames } from '@bifeai/shared';
import { useSyncExternalStore } from 'react';
import { getHardware, SCALE_REQUEST_BYTES, setHardware, type ScaleLinkConfig, type ScaleRequest } from './hardware';

/**
 * Balanza conectada a la computadora por cable (USB o RS-232 con adaptador).
 * Usa Web Serial de Chrome/Edge: no hay que instalar nada. La primera vez Chrome pregunta
 * qué puerto usar; después se reconecta solo.
 */
export type ScaleStatus = 'unsupported' | 'off' | 'needs-port' | 'connecting' | 'connected' | 'error';

export interface ScaleState {
  status: ScaleStatus;
  /** Último peso leído (gramos). null = todavía no llegó nada. */
  grams: number | null;
  stable: boolean;
  /** Última línea recibida, para la pantalla de prueba. */
  raw: string;
  /** Hora de la última lectura (ms). */
  at: number;
  error: string | null;
}

const supported = typeof navigator !== 'undefined' && 'serial' in navigator && !!navigator.serial;
let state: ScaleState = { status: supported ? 'off' : 'unsupported', grams: null, stable: false, raw: '', at: 0, error: null };
const listeners = new Set<() => void>();
function emit(patch: Partial<ScaleState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function useScale(): ScaleState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state,
  );
}

export function getScale(): ScaleState {
  return state;
}

export const scaleSupported = supported;

let port: SerialPort | null = null;
let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
let poll: ReturnType<typeof setInterval> | null = null;
let running = false;
const decoder = new TextDecoder('latin1');

function serialOptions(c: ScaleLinkConfig): SerialOptions {
  return { baudRate: c.baudRate, dataBits: c.dataBits, parity: c.parity, stopBits: c.stopBits };
}

async function write(p: SerialPort, bytes: number[]) {
  if (!bytes.length || !p.writable) return;
  const w = p.writable.getWriter();
  try {
    await w.write(new Uint8Array(bytes));
  } finally {
    w.releaseLock();
  }
}

/** Busca un puerto ya autorizado (el mismo cable de la última vez si se puede). */
async function grantedPort(c: ScaleLinkConfig): Promise<SerialPort | null> {
  if (!navigator.serial) return null;
  const ports = await navigator.serial.getPorts();
  if (!ports.length) return null;
  const same = ports.find((p) => {
    const i = p.getInfo();
    return c.usbVendorId !== undefined && i.usbVendorId === c.usbVendorId && i.usbProductId === c.usbProductId;
  });
  return same ?? ports[0] ?? null;
}

function friendlyError(err: unknown): string {
  const name = (err as { name?: string })?.name;
  if (name === 'NetworkError' || name === 'InvalidStateError')
    return 'No se pudo abrir el puerto: puede estar usándolo otro programa u otra pestaña de BifeAI. Cerralos y probá de nuevo.';
  if (name === 'NotFoundError') return 'No elegiste ningún puerto.';
  if (name === 'SecurityError') return 'El navegador no dio permiso para usar el puerto.';
  return 'Se cortó la conexión con la balanza. Revisá el cable.';
}

/** Desconecta (por ejemplo al cambiar la configuración). */
export async function disconnectScale(): Promise<void> {
  running = false;
  if (poll) clearInterval(poll);
  poll = null;
  try {
    await reader?.cancel();
  } catch {
    /* ya estaba cerrado */
  }
  reader = null;
  try {
    await port?.close();
  } catch {
    /* ya estaba cerrado */
  }
  port = null;
  if (supported) emit({ status: getHardware().scale.enabled ? 'needs-port' : 'off', grams: null, stable: false });
}

let pending: Promise<boolean> | null = null;

/** Abre el puerto y empieza a leer. Si `ask`, muestra el cuadro de Chrome para elegir el cable. */
export function connectScale(ask = false): Promise<boolean> {
  // Dos pantallas pueden pedir conectar a la vez: se comparte el mismo intento.
  if (pending) return pending;
  pending = doConnect(ask).finally(() => {
    pending = null;
  });
  return pending;
}

async function doConnect(ask: boolean): Promise<boolean> {
  if (!supported || !navigator.serial) return false;
  const c = getHardware().scale;
  if (!c.enabled) return false;
  if (running && port) return true;
  emit({ status: 'connecting', error: null });
  try {
    const p = ask ? await navigator.serial.requestPort() : await grantedPort(c);
    if (!p) {
      emit({ status: 'needs-port' });
      return false;
    }
    if (ask) {
      const info = p.getInfo();
      setHardware({ ...getHardware(), scale: { ...c, usbVendorId: info.usbVendorId, usbProductId: info.usbProductId } });
    }
    await p.open(serialOptions(c));
    port = p;
    running = true;
    emit({ status: 'connected', grams: null, stable: false, raw: '' });
    void readLoop(p, c);
    return true;
  } catch (err) {
    port = null;
    running = false;
    emit({ status: 'error', error: friendlyError(err) });
    return false;
  }
}

async function readLoop(p: SerialPort, c: ScaleLinkConfig) {
  const stability = new ScaleStability();
  const req = SCALE_REQUEST_BYTES[c.request];
  if (req.length) poll = setInterval(() => void write(p, req).catch(() => undefined), 250);
  let buffer = '';
  while (running && p.readable) {
    reader = p.readable.getReader();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { frames, rest } = splitScaleFrames(buffer);
        buffer = rest;
        for (const f of frames) {
          const r = parseScaleFrame(f, c.unit);
          if (!r) continue;
          const now = Date.now();
          emit({ grams: r.grams, stable: stability.push(r, now), raw: f.trim(), at: now });
        }
      }
    } catch (err) {
      if (running) emit({ status: 'error', error: friendlyError(err) });
      running = false;
    } finally {
      reader?.releaseLock();
      reader = null;
    }
  }
  if (poll) clearInterval(poll);
  poll = null;
}

/** Si la balanza dejó de mandar datos hace rato, el peso no sirve. */
export function scaleFresh(s: ScaleState, now = Date.now()): boolean {
  return s.status === 'connected' && s.grams !== null && now - s.at < 1500;
}

// ---------------------------------------------------------------- detección automática
export interface DetectStep {
  label: string;
  index: number;
  total: number;
}

const FRAMINGS: Pick<ScaleLinkConfig, 'baudRate' | 'dataBits' | 'parity' | 'stopBits'>[] = [
  { baudRate: 9600, dataBits: 8, parity: 'none', stopBits: 1 },
  { baudRate: 4800, dataBits: 8, parity: 'none', stopBits: 1 },
  { baudRate: 2400, dataBits: 8, parity: 'none', stopBits: 1 },
  { baudRate: 19200, dataBits: 8, parity: 'none', stopBits: 1 },
  { baudRate: 9600, dataBits: 7, parity: 'even', stopBits: 1 },
  { baudRate: 4800, dataBits: 7, parity: 'even', stopBits: 1 },
  { baudRate: 2400, dataBits: 7, parity: 'even', stopBits: 1 },
];
const REQUESTS: ScaleRequest[] = ['none', 'ENQ', 'W', 'P', 'DOLLAR'];

/** Escucha un rato con una configuración: ¿llegan pesos que se entienden? */
async function listen(p: SerialPort, c: ScaleLinkConfig, ms: number): Promise<number | null> {
  await p.open(serialOptions(c));
  const req = SCALE_REQUEST_BYTES[c.request];
  let good = 0;
  let last: number | null = null;
  let buffer = '';
  const r = p.readable!.getReader();
  const timer = setTimeout(() => void r.cancel().catch(() => undefined), ms);
  const ask = req.length ? setInterval(() => void write(p, req).catch(() => undefined), 200) : null;
  if (req.length) await write(p, req).catch(() => undefined);
  try {
    for (;;) {
      const { value, done } = await r.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { frames, rest } = splitScaleFrames(buffer);
      buffer = rest;
      for (const f of frames) {
        const x = parseScaleFrame(f, c.unit);
        if (x) {
          good++;
          last = x.grams;
        }
      }
      if (good >= 2) break;
    }
  } catch {
    /* sin datos */
  } finally {
    clearTimeout(timer);
    if (ask) clearInterval(ask);
    r.releaseLock();
    await p.close().catch(() => undefined);
  }
  return good >= 2 ? last : null;
}

/**
 * Prueba las velocidades y pedidos más comunes hasta que la balanza responda.
 * Devuelve la configuración que anduvo (y la guarda) o null.
 */
export async function detectScale(onStep: (s: DetectStep) => void): Promise<{ config: ScaleLinkConfig; grams: number } | null> {
  if (!supported || !navigator.serial) return null;
  await disconnectScale();
  const base = { ...getHardware().scale, enabled: true };
  let p: SerialPort;
  try {
    p = await navigator.serial.requestPort();
  } catch (err) {
    emit({ status: 'error', error: friendlyError(err) });
    return null;
  }
  const info = p.getInfo();
  // Primero se escucha sin pedir (la mayoría manda el peso sola); después pidiendo.
  const combos: ScaleLinkConfig[] = [];
  for (const request of REQUESTS) for (const f of FRAMINGS) combos.push({ ...base, ...f, request });
  for (const [i, c] of combos.entries()) {
    onStep({
      label: `${c.baudRate} baudios · ${c.dataBits}${c.parity === 'none' ? 'N' : 'E'}${c.stopBits}${c.request === 'none' ? '' : ' · pidiendo el peso'}`,
      index: i + 1,
      total: combos.length,
    });
    try {
      const grams = await listen(p, c, c.request === 'none' ? 1200 : 900);
      if (grams !== null) {
        const config = { ...c, usbVendorId: info.usbVendorId, usbProductId: info.usbProductId };
        setHardware({ ...getHardware(), scale: config });
        await connectScale();
        return { config, grams };
      }
    } catch (err) {
      emit({ status: 'error', error: friendlyError(err) });
      return null;
    }
  }
  emit({ status: 'needs-port' });
  return null;
}
